import { useTranslation } from 'react-i18next'
import { Globe, ChevronDown, Check } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const LanguageSwitcher = ({ isMobile = false }) => {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  const languages = [
    { code: 'en', name: 'EN', fullName: 'English' },
    { code: 'ar', name: 'AR', fullName: 'العربية' },
    { code: 'ku', name: 'KU', fullName: 'کوردی' },
    { code: 'de', name: 'DE', fullName: 'Deutsch' }
  ]

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng)
    document.documentElement.dir = lng === 'ar' || lng === 'ku' ? 'rtl' : 'ltr'
    setIsOpen(false)
  }

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0]

  // Close dropdown when clicking outside (desktop only)
  useEffect(() => {
    if (isMobile) return

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isOpen, isMobile])

  // Mobile variant
  const mobileMenuVariants = {
    hidden: { opacity: 0, height: 0, transition: { duration: 0.3 } },
    visible: { opacity: 1, height: "auto", transition: { duration: 0.4 } },
  }

  // Mobile View
  if (isMobile) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between text-white font-bold py-2 hover:text-white/80 transition-colors duration-300"
        >
          {currentLang.fullName}
          <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={mobileMenuVariants}
              className="ml-4 pt-2 space-y-2 overflow-hidden"
            >
              {languages.map((lang) => {
                const isActive = i18n.language === lang.code

                return (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`block w-full text-left py-1 transition-colors duration-300 text-base font-medium border-l-2 pl-4 ${isActive
                        ? 'text-white border-cyan-400'
                        : 'text-white/70 hover:text-white border-white/20 hover:border-white'
                      }`}
                  >
                    {lang.fullName}
                  </button>
                )
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  // Desktop View
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Main Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl hover:bg-white/10 transition-all duration-300 px-3 sm:px-4 py-2 sm:py-2.5 shadow-lg hover:shadow-xl hover:border-white/20 min-w-[70px] sm:min-w-[90px]"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        <div className="relative flex items-center justify-center gap-1.5 sm:gap-2">
          <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/80 group-hover:text-white transition-colors group-hover:rotate-12 duration-300" />
          <span className="text-xs sm:text-sm font-semibold tracking-wide text-white">
            {currentLang.name}
          </span>
          <ChevronDown
            className={`w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/60 group-hover:text-white transition-all duration-300 ${isOpen ? 'rotate-180' : ''
              }`}
          />
        </div>
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm sm:bg-transparent sm:backdrop-blur-none"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-full right-0 mt-2 sm:mt-3 py-1.5 sm:py-2 bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl sm:rounded-2xl shadow-2xl min-w-[160px] sm:min-w-[180px] z-50 overflow-hidden"
            >
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] via-transparent to-transparent pointer-events-none" />

              {languages.map((lang, index) => {
                const isActive = i18n.language === lang.code

                return (
                  <motion.button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`relative w-full px-3 sm:px-4 py-2.5 sm:py-3 text-left transition-all duration-200 flex items-center justify-between group touch-manipulation ${isActive
                        ? 'text-white bg-white/10 font-semibold'
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    {/* Active indicator bar */}
                    {isActive && (
                      <motion.div
                        layoutId="activeLanguage"
                        className="absolute left-0 top-0 bottom-0 w-0.5 sm:w-1 bg-gradient-to-b from-blue-500 via-cyan-400 to-blue-500"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}

                    <div className="flex items-center gap-2 sm:gap-3 flex-1">
                      {/* Language code badge */}
                      <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                        <span className="text-[10px] sm:text-xs font-bold text-white/90 tracking-wider">
                          {lang.name}
                        </span>
                      </div>

                      {/* Language name */}
                      <div className="flex flex-col">
                        <span className="text-xs sm:text-sm font-medium leading-tight">
                          {lang.fullName}
                        </span>
                      </div>
                    </div>

                    {/* Check icon for active language */}
                    {isActive && (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 500, damping: 25 }}
                      >
                        <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-400" strokeWidth={3} />
                      </motion.div>
                    )}

                    {/* Hover effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  </motion.button>
                )
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default LanguageSwitcher